import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import clsx from "clsx";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CloudUpload,
  Download,
  Eye,
  FilePlus,
  FileText,
  Layers,
  Moon,
  Play,
  Plus,
  Scissors,
  SlidersHorizontal,
  Sun,
  Terminal,
  Terminal as TerminalIcon,
  Trash2,
  UploadCloud,
  X,
  ZoomIn,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Slider } from "./components/ui/slider";
import { Switch } from "./components/ui/switch";
import "./index.css";
import {
  formatPageSpecification,
  parsePageSpecification,
  stitchPdfHalves,
} from "./lib/pdfStitcher";

// Interface for files in the stitching queue
interface FileItem {
  id: string;
  file: File;
  name: string;
  size: number;
  arrayBuffer: ArrayBuffer;
  pdfDocJs: any; // PDFJS doc if loaded
  totalPages: number;
  exclusionSpec: string;
  side: "left" | "right";
  splitRatio: number; // 0.1 to 0.9 (defaults to 0.5)
  outerMargin: number; // 0 to 100 pt (defaults to 0)
  gutter: number; // 0 to 100 pt (defaults to 0)
  tbPadding: number; // 0 to 100 pt (defaults to 0)
  leftPadding: number; // left page offset/padding (defaults to 0)
  rightPadding: number; // right page offset/padding (defaults to 0)
  status: "idle" | "processing" | "success" | "error";
  error?: string;
  resultBytes?: Uint8Array;
}

// Log message interface
interface LogMessage {
  id: string;
  timestamp: string;
  type: "info" | "success" | "error" | "warn";
  text: string;
}

export function App() {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [pdfjs, setPdfjs] = useState<any>(null);
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [logOpen, setLogOpen] = useState(true);
  const [autoScrollLogs, setAutoScrollLogs] = useState(true);

  // Layout states
  const [advancedMode, setAdvancedMode] = useState(true);
  const [logsVisible, setLogsVisible] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<"sm" | "md" | "lg">("md");

  // Fullscreen preview states
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Load theme preference on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    const initialTheme = savedTheme || "dark";
    setTheme(initialTheme);
    if (initialTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    addLog("Application initialized.", "info");
  }, []);

  // Toggle Theme
  const toggleTheme = () => {
    const nextTheme = theme === "light" ? "dark" : "light";
    setTheme(nextTheme);
    localStorage.setItem("theme", nextTheme);
    if (nextTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  // Load PDF.js from CDN dynamically to handle thumbnails
  useEffect(() => {
    if ((window as any).pdfjsLib) {
      setPdfjs((window as any).pdfjsLib);
      addLog("PDFJS library detected from window context.", "info");
      return;
    }

    addLog("Loading PDFJS rendering engine from CDN...", "info");
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.async = true;
    script.onload = () => {
      const lib = (window as any).pdfjsLib;
      lib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      setPdfjs(lib);
      addLog("PDFJS rendering engine loaded successfully.", "success");
    };
    script.onerror = () => {
      addLog(
        "Failed to load PDFJS. Falling back to default schematic UI placeholders.",
        "warn",
      );
    };
    document.body.appendChild(script);
  }, []);

  // Auto scroll logs
  useEffect(() => {
    if (autoScrollLogs && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScrollLogs]);

  useEffect(() => {
    import("pdf-lib").then((module) => {
      // console.log("pdf-lib loaded", module);
    });
  }, []);

  // Handle preview URL generation/cleanup
  const previewFile = files.find((f) => f.id === previewFileId);
  useEffect(() => {
    if (previewFile?.resultBytes) {
      const blob = new Blob([previewFile.resultBytes], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      addLog(`Created preview URL for "${previewFile.name}".`, "info");
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setPreviewUrl(null);
    }
  }, [previewFileId, previewFile?.resultBytes]);

  // Log helper
  const addLog = (
    text: string,
    type: "info" | "success" | "error" | "warn" = "info",
  ) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [
      ...prev,
      {
        id: Math.random().toString(36).substring(7),
        timestamp,
        type,
        text,
      },
    ]);
  };

  const clearLogs = () => {
    setLogs([]);
    addLog("Logs cleared.", "info");
  };

  // Format File Size
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Load file and calculate details
  const handleFilesAdded = async (fileList: FileList) => {
    addLog(`Adding ${fileList.length} files...`, "info");
    const newItems: FileItem[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file.type !== "application/pdf" && !file.name.endsWith(".pdf")) {
        addLog(`Skipped: "${file.name}" (not a PDF file)`, "warn");
        continue;
      }

      addLog(`Reading file structure: "${file.name}"...`, "info");
      try {
        const arrayBuffer = await file.arrayBuffer();

        // Use PDFDocument to verify loading and get page count
        const { PDFDocument } = await import("pdf-lib");
        const docLib = await PDFDocument.load(arrayBuffer.slice(0), {
          updateMetadata: false,
        });
        const totalPages = docLib.getPageCount();

        let pdfDocJs = null;
        if (pdfjs) {
          try {
            // Slice the buffer to prevent PDFJS from detaching it
            const loadingTask = pdfjs.getDocument({
              data: new Uint8Array(arrayBuffer.slice(0)),
            });
            pdfDocJs = await loadingTask.promise;
          } catch (err) {
            console.error("PDFJS load failed:", err);
          }
        }

        const newItem: FileItem = {
          id: Math.random().toString(36).substring(7),
          file,
          name: file.name,
          size: file.size,
          arrayBuffer,
          pdfDocJs,
          totalPages,
          exclusionSpec: "",
          side: "right", // Default is right
          splitRatio: 0.505,
          outerMargin: 0,
          gutter: 0,
          tbPadding: 0,
          leftPadding: 0,
          rightPadding: 0,
          status: "idle",
        };

        newItems.push(newItem);
        addLog(
          `Successfully loaded "${file.name}" (${totalPages} pages, size ${formatBytes(file.size)})`,
          "success",
        );
      } catch (err: any) {
        addLog(`Error parsing "${file.name}": ${err?.message || err}`, "error");
      }
    }

    if (newItems.length > 0) {
      setFiles((prev) => {
        const updated = [...prev, ...newItems];
        // Set active file if none selected
        if (!activeFileId) {
          setActiveFileId(newItems[0].id);
        }
        return updated;
      });
    }
  };

  const removeFile = (id: string, name: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    addLog(`Removed "${name}" from queue.`, "info");
    if (activeFileId === id) {
      setActiveFileId(null);
    }
  };

  // Drag-and-drop Handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files) {
      handleFilesAdded(e.dataTransfer.files);
    }
  };

  // Update active file settings
  const updateActiveFile = (updates: Partial<FileItem>) => {
    if (!activeFileId) return;
    setFiles((prev) =>
      prev.map((f) =>
        f.id === activeFileId
          ? {
              ...f,
              ...updates,
              status:
                f.status === "success" || f.status === "error"
                  ? "idle"
                  : f.status,
              resultBytes:
                f.status === "success" || f.status === "error"
                  ? undefined
                  : f.resultBytes,
            }
          : f,
      ),
    );
  };

  const activeFile = files.find((f) => f.id === activeFileId);

  // Toggle page exclusion visually
  const handleTogglePageExclusion = (pageIdx: number) => {
    if (!activeFile) return;

    try {
      const excludedSet = parsePageSpecification(
        activeFile.exclusionSpec,
        activeFile.totalPages,
      );
      if (excludedSet.has(pageIdx)) {
        excludedSet.delete(pageIdx);
      } else {
        excludedSet.add(pageIdx);
      }

      const nextSpec = formatPageSpecification(excludedSet);
      updateActiveFile({ exclusionSpec: nextSpec });
      addLog(
        `Updated exclusion spec to "${nextSpec || "none"}" for "${activeFile.name}"`,
        "info",
      );
    } catch (err: any) {
      addLog(`Exclusion toggle error: ${err.message}`, "error");
    }
  };

  // Preset exclusion strings
  const applyPreset = (preset: "none" | "cover" | "common" | "ends") => {
    if (!activeFile) return;

    let spec = "";
    if (preset === "cover") {
      spec = "1";
    } else if (preset === "common") {
      if (activeFile.totalPages >= 48) {
        spec = `1, ${activeFile.totalPages - 2}, ${activeFile.totalPages - 1}, ${activeFile.totalPages}`;
      } else {
        spec = "1";
      }
    } else if (preset === "ends") {
      spec = `1, ${activeFile.totalPages}`;
    }

    updateActiveFile({ exclusionSpec: spec });
    addLog(
      `Applied exclusion preset "${preset}" -> "${spec || "none"}" for "${activeFile.name}"`,
      "info",
    );
  };

  const reprocessTimerRef = useRef<any>(null);

  // Updates active file settings from preview and triggers debounced re-stitching
  const updatePreviewSetting = (updates: Partial<FileItem>) => {
    if (!previewFileId) return;

    // Update state immediately so UI sliders move smoothly
    setFiles((prev) =>
      prev.map((f) => (f.id === previewFileId ? { ...f, ...updates } : f)),
    );

    // Debounce the background stitching call
    if (reprocessTimerRef.current) {
      clearTimeout(reprocessTimerRef.current);
    }

    reprocessTimerRef.current = setTimeout(() => {
      setFiles((prev) => {
        const item = prev.find((f) => f.id === previewFileId);
        if (item) {
          processFile(item, false);
        }
        return prev;
      });
    }, 300);
  };

  // Process a single file
  const processFile = async (item: FileItem, autoPreview: boolean = false) => {
    // Update status to processing
    setFiles((prev) =>
      prev.map((f) =>
        f.id === item.id ? { ...f, status: "processing", error: undefined } : f,
      ),
    );
    addLog(`Starting stitching process for "${item.name}"...`, "info");

    try {
      const startTime = performance.now();
      // Slice the buffer to prevent detaching/mutation
      const outputBytes = await stitchPdfHalves(
        new Uint8Array(item.arrayBuffer.slice(0)),
        {
          exclusionSpec: item.exclusionSpec,
          side: item.side,
          splitRatio: item.splitRatio,
          outerMargin: item.outerMargin,
          gutter: item.gutter,
          tbPadding: item.tbPadding,
          leftPadding: item.leftPadding,
          rightPadding: item.rightPadding,
          onProgress: (msg) => addLog(`[${item.name}] ${msg}`, "info"),
        },
      );

      const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
      addLog(`Stitch complete for "${item.name}" in ${elapsed}s!`, "success");

      setFiles((prev) =>
        prev.map((f) =>
          f.id === item.id
            ? {
                ...f,
                status: "success",
                resultBytes: outputBytes,
              }
            : f,
        ),
      );

      if (autoPreview) {
        setPreviewFileId(item.id);
      }
    } catch (err: any) {
      const errMsg = err?.message || err;
      addLog(`Stitching failed for "${item.name}": ${errMsg}`, "error");
      setFiles((prev) =>
        prev.map((f) =>
          f.id === item.id ? { ...f, status: "error", error: errMsg } : f,
        ),
      );
    }
  };

  // Download output
  const downloadResult = (item: FileItem) => {
    if (!item.resultBytes) return;
    addLog(`Downloading result for "${item.name}"...`, "info");

    const blob = new Blob([item.resultBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const capitalizedSide =
      item.side.charAt(0).toUpperCase() + item.side.slice(1);
    a.download = `EOnly-${capitalizedSide}-${item.name}`;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Run all files in the list
  const runBatchProcessing = async () => {
    if (files.length === 0) {
      addLog("No files to process in batch.", "warn");
      return;
    }

    addLog(`=== Starting Batch Stitching (${files.length} files) ===`, "info");
    for (const file of files) {
      await processFile(file);
    }
    addLog("=== Batch Stitching Complete ===", "success");
  };

  // Calculate pages list helper
  const getPagesIndices = (total: number, spec: string) => {
    const indices = { excluded: new Set<number>(), valid: [] as number[] };
    try {
      indices.excluded = parsePageSpecification(spec, total);
    } catch (e) {}

    for (let i = 0; i < total; i++) {
      if (!indices.excluded.has(i)) {
        indices.valid.push(i);
      }
    }
    return indices;
  };

  const activePages = activeFile
    ? getPagesIndices(activeFile.totalPages, activeFile.exclusionSpec)
    : null;

  useEffect(() => {
    if (previewFile && previewUrl) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.removeAttribute("style");
    }
  }, [previewFile, previewUrl]);

  return (
    <div className="min-h-screen w-full flex flex-col bg-background text-foreground transition-colors duration-300">
      {/* Background Subtle Elements */}
      <div className="absolute inset-0 bg-radial-gradient from-primary/5 via-transparent to-transparent pointer-events-none z-0" />

      {/* Top Header */}
      <header className="relative w-[100vw] z-10  backdrop-blur-md px-8 pt-4 pb-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 text-primary p-2.5 rounded-lg border border-primary/20 flex items-center justify-center">
            <Layers className="size-6 animate-in fade-in duration-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight bg-linear-to-r from-primary to-violet-500 bg-clip-text text-transparent flex items-center gap-2">
              StitchPDF{" "}
              <span className="text-[10px] uppercase font-mono tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full border border-primary/10">
                v1.6
              </span>
            </h1>
            <p className="text-xs text-muted-foreground">
              {/* Crop left/right halves and stitch side-by-side with selectable
              text */}
              Copyright © 2026{" "}
              <a
                // href="https://abdulsamadmalik.github.io"
                href="https://github.com/AbdulSamadMalik"
                className="underline decoration-transparent underline-offset-2 hover:text-primary transition hover:decoration-primary"
                target="_blank"
              >
                AbdulSamadMalik
              </a>
              . All rights reserved.
            </p>
          </div>
        </div>

        {/* Toggle Controls: Advanced Split layout & Theme Switch */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3 border border-border bg-card/30 rounded-lg pl-3 pr-2 py-1.5 shadow-xs">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              {/* <SlidersHorizontal className="size-3.5 text-primary" /> */}
              <Terminal className="size-3.5 text-primary" />
              {/* Advanced Split */}
              Show Logs
            </span>
            <Switch
              // checked={advancedMode}
              // onCheckedChange={(checked) => {
              //   setAdvancedMode(checked);
              //   addLog(`Layout toggled. Advanced Split: ${checked}`, "info");
              // }}
              checked={logsVisible}
              onCheckedChange={(checked) => {
                setLogsVisible(checked);
                // addLog(`Logs: ${checked}`, "info");
              }}
            />

            {/* <button
              onClick={() => {
                setAdvancedMode(!advancedMode);
                addLog(
                  `Layout toggled. Advanced Split: ${!advancedMode}`,
                  "info",
                );
              }}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                advancedMode ? "bg-primary" : "bg-muted"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-background shadow-md transition duration-200 ease-in-out ${
                  advancedMode ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button> */}
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full w-9 h-9 border border-border"
            title="Toggle theme"
          >
            {theme === "dark" ? (
              <Sun className="size-4" />
            ) : (
              <Moon className="size-4" />
            )}
          </Button>
        </div>
      </header>

      {/* Main Content Area (Full screen width & height layout) */}
      <main className="relative  z-10 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-3 px-6 w-full max-w-[100vw] px-8 overflow-hidden h-[calc(100vh-140px)]">
        {/* ========================================================================= */}
        {/* LEFT COLUMN: Files list, uploads, and in Advanced mode, Geometry settings too */}
        {/* ========================================================================= */}
        <div className="lg:col-span-4 flex flex-col gap-5 h-full overflow-y-auto pr-2 pb-6">
          {/* File Upload card */}
          <Card
            className={clsx(
              `border-2 shadow-none border-dashed shrink-0 transition-all duration-300`,
              isDragOver
                ? "border-primary bg-primary/5 scale-[1.01]"
                : "border-border hover:border-primary/50 bg-card/40",

              // files.length > 0 && "hidden",
              "hidden",
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent
              className="p-5 flex flex-col items-center justify-center text-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept=".pdf"
                className="hidden"
                onChange={(e) =>
                  e.target.files && handleFilesAdded(e.target.files)
                }
              />
              <UploadCloud className="size-7 text-primary/80 mb-2" />
              <h3 className="text-xs font-bold">Drag & Drop PDFs Here</h3>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Click to browse files.
              </p>
            </CardContent>
          </Card>

          {/* Files Queue List */}
          <Card
            className={clsx(
              "bg-card/40 shadow-none backdrop-blur-xs overflow-hidden shrink-0 gap-1.5 pb-2 pt-0",
              files.length == 0 && "flex-1",
            )}
          >
            <CardHeader className="py-3! px-4 gap-0 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold uppercase">
                    Document Queue
                  </CardTitle>
                </div>
                {/* {files.length > 0 && (
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-7 text-[10px] font-bold gap-1 shadow-xs border-primary/20 hover:bg-primary/5 hover:text-primary"
                    onClick={runBatchProcessing}
                  >
                    <Play className="size-2.5 fill-current" />
                    Process Queue
                  </Button>
                )} */}

                <Button
                  size="lg"
                  variant="outline"
                  className={clsx(
                    // files.length == 0 && "hidden",
                    "h-7 text-[12px] font-bold gap-1 shadow-xs border-primary/20 hover:bg-primary/5 hover:text-primary",
                  )}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Plus />
                  Add Files
                </Button>
              </div>
            </CardHeader>
            <CardContent
              className={clsx(
                "p-0 overflow-y-auto",
                files.length == 0 && "flex flex-1 justify-center items-center",
              )}
            >
              {files.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
                  {/* <FileText className="size-16 opacity-50 mb-4" />
                  <p className="text-sm font-medium ">Queue is empty</p> */}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {files.map((item) => {
                    const isActive = item.id === activeFileId;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setActiveFileId(item.id)}
                        className={`p-2 flex items-center justify-between gap-3 cursor-pointer transition-all duration-200 ${
                          isActive
                            ? "bg-green-600/20 dark:bg-white/10 border-0 border-l-4 border-green-900 dark:border-white"
                            : "hover:bg-muted/30 border-l-4 border-transparent"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="font-semibold text-xs truncate max-w-[150px]">
                              {item.name}
                            </span>
                            <span className="text-[8px] bg-muted px-1 py-0.1 rounded font-mono text-muted-foreground">
                              {item.totalPages}p
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-[9px] text-muted-foreground font-mono flex-wrap">
                            <span>{formatBytes(item.size)}</span>
                            <span>•</span>
                            <span className="capitalize text-primary font-semibold">
                              {item.side}
                            </span>
                          </div>
                        </div>

                        <div
                          className="flex items-center gap-1 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.status === "success" && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="size-8 text-primary hover:bg-primary/10 hover:text-primary rounded"
                              onClick={() => setPreviewFileId(item.id)}
                              title="Preview PDF"
                            >
                              <Eye className="size-4" />
                            </Button>
                          )}
                          {item.status === "idle" && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="size-8 text-primary hover:bg-primary/10 hover:text-primary rounded"
                              onClick={() => processFile(item, true)}
                            >
                              <Play className="size-4 fill-current" />
                            </Button>
                          )}
                          {item.status === "processing" && (
                            <div className="size-8 flex items-center justify-center">
                              <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                          {item.status === "success" && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="size-8 dark:text-green-300 dark:hover:text-green-200 text-green-700 hover:bg-green-500/10 dark:hover:bg-green-100/20 hover:text-green-800 rounded"
                              onClick={() => downloadResult(item)}
                              title="Download PDF"
                            >
                              <Download className="size-4" />
                            </Button>
                          )}
                          {item.status === "error" && (
                            <div
                              className="size-8 flex items-center justify-center text-destructive"
                              title={item.error}
                            >
                              <AlertTriangle className="size-4" />
                            </div>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="size-8 hover:bg-destructive/10 hover:text-destructive rounded"
                            onClick={() => removeFile(item.id, item.name)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ADVANCED LAYOUT ONLY: Stacking Stitching Configuration & Geometry on the left */}
          {advancedMode && activeFile && (
            <Card className="bg-card/40 pt-0 shadow-none shrink-0 flex-1 gap-0 pb-5">
              <CardHeader className="pt-3 pb-3! px-4 gap-0! border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                    {/* <Settings className="size-3.5" /> */}
                    Split Configuration
                  </CardTitle>
                  {/* {activeFile.status === "success" && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px] px-2 shadow-xs border-primary/30 text-primary hover:bg-primary/5"
                        onClick={() => setPreviewFileId(activeFile.id)}
                      >
                        <Eye className="size-3 mr-1" />
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white font-bold h-7 text-[10px] px-2 shadow-xs"
                        onClick={() => downloadResult(activeFile)}
                      >
                        <Download className="size-3 mr-1" />
                        Download
                      </Button>
                    </div>
                  )} */}
                </div>
              </CardHeader>
              <CardContent className="p-4 flex-1 pb-0 flex flex-col gap-4">
                {/* Split Side & Exclusions */}
                <div className="flex flex-col gap-3">
                  {/* Select split side */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Crop Split Side
                    </label>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={() => updateActiveFile({ side: "left" })}
                        className={`py-1.5 rounded-full border text-[11px] font-semibold transition-all ${
                          activeFile.side === "left"
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:bg-muted/30 text-muted-foreground"
                        }`}
                      >
                        Left Half
                      </Button>
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={() => updateActiveFile({ side: "right" })}
                        className={`py-1.5 rounded-full border text-[11px] font-semibold transition-all ${
                          activeFile.side === "right"
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:bg-muted/30 text-muted-foreground"
                        }`}
                      >
                        Right Half
                      </Button>
                    </div>
                  </div>

                  {/* Exclusions text area */}
                  <div className="flex flex-col gap-1 hidden">
                    <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                      Exclude Pages
                    </label>
                    <Input
                      placeholder="e.g. 1,46,47-48"
                      value={activeFile.exclusionSpec}
                      onChange={(e) =>
                        updateActiveFile({ exclusionSpec: e.target.value })
                      }
                      className="h-8 text-xs font-mono"
                    />
                    <div className="flex gap-1 flex-wrap mt-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-4.5 px-1 text-[8px] bg-muted/40 hover:bg-muted font-medium"
                        onClick={() => applyPreset("none")}
                      >
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-4.5 px-1 text-[8px] bg-muted/40 hover:bg-muted font-medium"
                        onClick={() => applyPreset("cover")}
                      >
                        Cover
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-4.5 px-1 text-[8px] bg-muted/40 hover:bg-muted font-medium"
                        onClick={() => applyPreset("common")}
                      >
                        Ends
                      </Button>
                    </div>
                  </div>
                </div>

                {/* GEOMETRY SLIDERS Stacked on Left column in Advanced Mode */}
                <div className="flex flex-col gap-7 mt-2 rounded-lg p-0">
                  {/* <div className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-2">
                    <SlidersHorizontal className="size-4" />
                    Stitching Geometry Settings
                  </div> */}

                  {/* Split Ratio Slider */}
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between text-[11px] font-medium">
                      <span className="text-muted-foreground">Split Ratio</span>
                      <span className="text-primary font-mono font-bold">
                        {(activeFile.splitRatio * 100).toFixed(1)}
                      </span>
                    </div>
                    {/* <input
                      type="range"
                      min="0.4"
                      max="0.8"
                      step="0.005"
                      value={activeFile.splitRatio}
                      onChange={(e) =>
                        updateActiveFile({
                          splitRatio: parseFloat(e.target.value),
                        })
                      }
                      className="w-full accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer"
                    /> */}
                    <Slider
                      defaultValue={[activeFile.splitRatio]}
                      min={0.4}
                      max={0.8}
                      step={0.005}
                      onValueChange={(val) =>
                        updateActiveFile({
                          splitRatio: val[0],
                        })
                      }
                      className="mx-auto w-full"
                    />
                  </div>

                  {/* Left Padding Slider */}
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between text-[11px] font-medium">
                      <span className="text-muted-foreground">
                        Left Padding
                      </span>
                      <span className="text-primary font-mono font-bold">
                        {activeFile.leftPadding || 0} pt
                      </span>
                    </div>
                    {/* <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={activeFile.leftPadding || 0}
                      onChange={(e) =>
                        updateActiveFile({
                          leftPadding: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer"
                    /> */}
                    <Slider
                      defaultValue={[activeFile.leftPadding]}
                      min={-100}
                      max={100}
                      step={1}
                      onValueChange={(val) =>
                        updateActiveFile({
                          leftPadding: val[0],
                        })
                      }
                      className="mx-auto w-full"
                    />
                  </div>

                  {/* Right Padding Slider */}
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between text-[11px] font-medium">
                      <span className="text-muted-foreground">
                        Right Padding
                      </span>
                      <span className="text-primary font-mono font-bold">
                        {activeFile.rightPadding || 0} pt
                      </span>
                    </div>
                    {/* <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={activeFile.rightPadding || 0}
                      onChange={(e) =>
                        updateActiveFile({
                          rightPadding: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer"
                    /> */}
                    <Slider
                      defaultValue={[activeFile.rightPadding]}
                      min={-100}
                      max={100}
                      step={1}
                      onValueChange={(val) =>
                        updateActiveFile({
                          rightPadding: val[0],
                        })
                      }
                      className="mx-auto w-full"
                    />
                  </div>

                  {/* Column Gutter Slider */}
                  <div className="flex flex-col gap-2 hidden">
                    <div className="flex justify-between text-[11px] font-medium">
                      <span className="text-muted-foreground">
                        Column Gutter (space)
                      </span>
                      <span className="text-primary font-mono font-bold">
                        {activeFile.gutter} pt
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={activeFile.gutter}
                      onChange={(e) =>
                        updateActiveFile({
                          gutter: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Outer Margin Slider */}
                  <div className="flex flex-col gap-2 hidden">
                    <div className="flex justify-between text-[11px] font-medium">
                      <span className="text-muted-foreground">
                        Outer Page Margin
                      </span>
                      <span className="text-primary font-mono font-bold">
                        {activeFile.outerMargin} pt
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={activeFile.outerMargin}
                      onChange={(e) =>
                        updateActiveFile({
                          outerMargin: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Top/Bottom Padding Slider */}
                  <div className="flex flex-col gap-2 hidden">
                    <div className="flex justify-between text-[11px] font-medium">
                      <span className="text-muted-foreground">
                        Top/Bottom Padding
                      </span>
                      <span className="text-primary font-mono font-bold">
                        {activeFile.tbPadding} pt
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={activeFile.tbPadding}
                      onChange={(e) =>
                        updateActiveFile({
                          tbPadding: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>

                {/* Main Process Button */}
                <div className="mt-auto">
                  {activeFile.status !== "processing" ? (
                    <Button
                      onClick={() => processFile(activeFile, true)}
                      className="w-full font-bold shadow-xs h-9 bg-accent-foreground text-xs"
                    >
                      <Play className="size-3 mr-1 fill-current" />
                      Stitch PDF Halves
                    </Button>
                  ) : (
                    <Button
                      disabled
                      className="w-full h-9 text-xs mt-1 font-bold"
                    >
                      <div className="size-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1.5" />
                      Stitching PDF...
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ========================================================================= */}
        {/* RIGHT COLUMN: dedicated page preview panel */}
        {/* ========================================================================= */}
        <div className="lg:col-span-8 flex flex-col gap-5 h-full overflow-hidden max-h-[calc(100vh-80px)] pb-6">
          {activeFile ? (
            <>
              {/* If NOT in advanced mode, show settings in right column. Otherwise, right column is 100% preview panel */}
              {!advancedMode ? (
                /* Standard Settings Card */
                <Card className="bg-card/40 shrink-0">
                  <CardHeader className="py-4 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-sm font-bold truncate max-w-[320px] md:max-w-md">
                          Configuration: {activeFile.name}
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Adjust stitching settings for the selected PDF
                        </CardDescription>
                      </div>
                      {activeFile.status === "success" && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs font-semibold gap-1.5"
                            onClick={() => setPreviewFileId(activeFile.id)}
                          >
                            <Eye className="size-3.5" />
                            Preview
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold gap-1.5 shadow-xs h-8 text-xs"
                            onClick={() => downloadResult(activeFile)}
                          >
                            <Download className="size-3.5" />
                            Download Result
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-5  flex flex-col gap-4">
                    {/* Exclude & Split side select */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Side Select */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                          <SlidersHorizontal className="size-3" />
                          Crop Side
                        </label>
                        <div className="grid grid-cols-2 gap-2 mt-0.5">
                          <button
                            type="button"
                            onClick={() => updateActiveFile({ side: "left" })}
                            className={`py-2 rounded border text-xs font-semibold transition-all ${
                              activeFile.side === "left"
                                ? "border-primary bg-primary/5 text-primary"
                                : "border-border hover:bg-muted/20 text-muted-foreground"
                            }`}
                          >
                            Left Half
                          </button>
                          <button
                            type="button"
                            onClick={() => updateActiveFile({ side: "right" })}
                            className={`py-2 rounded border text-xs font-semibold transition-all ${
                              activeFile.side === "right"
                                ? "border-primary bg-primary/5 text-primary"
                                : "border-border hover:bg-muted/20 text-muted-foreground"
                            }`}
                          >
                            Right Half
                          </button>
                        </div>
                      </div>

                      {/* Exclude Pages */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                          <Scissors className="size-3" />
                          Exclude Pages
                        </label>
                        <Input
                          placeholder="e.g. 1,46,47-48"
                          value={activeFile.exclusionSpec}
                          onChange={(e) =>
                            updateActiveFile({ exclusionSpec: e.target.value })
                          }
                          className="h-9 text-xs"
                        />
                        <div className="flex gap-1 mt-0.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-5 px-1.5 text-[9px] bg-muted/40 hover:bg-muted font-medium"
                            onClick={() => applyPreset("none")}
                          >
                            Clear
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-5 px-1.5 text-[9px] bg-muted/40 hover:bg-muted font-medium"
                            onClick={() => applyPreset("cover")}
                          >
                            Exclude Cover
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Standard Action button */}
                    {activeFile.status !== "processing" ? (
                      <Button
                        variant="default"
                        onClick={() => processFile(activeFile, true)}
                        className="w-full mt-1 font-bold shadow-xs py-4.5 bg-accent-foreground text-xs"
                      >
                        <Play className="size-3.5 mr-1.5 fill-current" />
                        Stitch PDF Halves
                      </Button>
                    ) : (
                      <Button
                        disabled
                        className="w-full mt-1 py-4.5 font-bold text-xs"
                      >
                        <div className="size-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        Processing PDF...
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : null}

              {/* MASSIVE PREVIEW PANEL (Dedicating full right column if advancedMode is active) */}
              <Card
                className={`flex pb-2! shadow-none flex-col gap-0 pt-4 bg-card/40 backdrop-blur-xs overflow-hidden ${advancedMode ? "flex-1" : "h-[320px]"}`}
              >
                <CardHeader className="pb-4! px-6 border-b flex flex-row items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    {/* <Eye className="size-4 text-primary" /> */}
                    <div>
                      <CardTitle className="text-sm font-bold">
                        Pages Selection & Preview Grid
                      </CardTitle>
                      <CardDescription className="text-[10px]">
                        Click pages to exclude them from the stitch output
                      </CardDescription>
                    </div>
                  </div>

                  {/* High quality Zoom controls for thumbnail sizing */}
                  <div className="flex items-center gap-3 border bg-card rounded-lg pl-3 pr-1 py-1 shadow-inner">
                    <ZoomIn className="size-3.5 text-muted-foreground" />
                    <span className="text-[10px] font-bold text-muted-foreground mr-1 uppercase">
                      Zoom
                    </span>
                    <div className="flex items-center gap-1 bg-muted/80 p-1 rounded-md">
                      <button
                        onClick={() => setZoomLevel("sm")}
                        className={`cursor-pointer px-3.5 py-[3px] text-[9.5px] font-bold rounded-sm transition-all ${
                          zoomLevel === "sm"
                            ? "bg-primary text-primary-foreground shadow-xs"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        Small
                      </button>
                      <button
                        onClick={() => setZoomLevel("md")}
                        className={`cursor-pointer px-3.5 py-[3px] text-[9.5px] font-bold rounded-sm transition-all ${
                          zoomLevel === "md"
                            ? "bg-primary text-primary-foreground shadow-xs"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        Medium
                      </button>
                      <button
                        onClick={() => setZoomLevel("lg")}
                        className={`cursor-pointer px-3.5 py-[3px] text-[9.5px] font-bold rounded-sm transition-all ${
                          zoomLevel === "lg"
                            ? "bg-primary text-primary-foreground shadow-xs"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        Large
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 pb-10 flex-1 overflow-y-auto bg-zinc-950/0">
                  {/* Visual indication showing Included vs Excluded details */}
                  <div className="flex justify-between items-center text-[10px] text-muted-foreground mb-4 font-mono font-bold bg-muted/30 border p-2 rounded-lg">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="size-3 text-green-500" />
                      Stitching:{" "}
                      <span className="text-primary">
                        {activePages?.valid.length || 0} pages
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <X className="size-3 text-destructive" />
                      Skipping:{" "}
                      <span className="text-destructive">
                        {activePages?.excluded.size || 0} page
                        {activePages?.excluded.size === 1 ? "" : "s"}
                      </span>
                    </span>
                  </div>

                  {/* Preview grid applying zoom levels */}
                  <div
                    className={`grid gap-4 ${
                      zoomLevel === "sm"
                        ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
                        : zoomLevel === "md"
                          ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
                          : "grid-cols-1 xl:grid-cols-2"
                    }`}
                  >
                    {Array.from({ length: activeFile.totalPages }, (_, i) => {
                      const pageNum = i + 1;
                      const isExcluded = activePages?.excluded.has(i) || false;
                      return (
                        <PageThumbnail
                          key={i}
                          pdfDoc={activeFile.pdfDocJs}
                          pageNumber={pageNum}
                          side={activeFile.side}
                          splitRatio={activeFile.splitRatio}
                          isExcluded={isExcluded}
                          zoom={zoomLevel}
                          onClick={() => handleTogglePageExclusion(i)}
                        />
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="flex-grow shadow-none flex flex-col items-center justify-center p-12 text-center text-muted-foreground bg-card/25 border-dashed border-2 gap-2">
              {/* <SlidersHorizontal className="size-12 opacity-15 mb-2" /> */}
              <FilePlus className="size-16 opacity-70 mb-4" />
              <h3 className="text-sm font-bold">No Active PDF Selected</h3>
              <p className="text-xs max-w-sm">
                {/* Select a document from the queue on the left to configure page
                ranges and run stitching. */}
                Select a document to get started.
              </p>

              <Button
                size="lg"
                className="mt-8 rounded-full"
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUpload className="size-5 stroke-[2px]" />
                Upload PDFs
              </Button>
            </Card>
          )}
        </div>
      </main>

      {/* Fullscreen PDF Preview Dialog */}
      {previewFile && previewUrl && (
        // md:p-3 lg:p-4
        <div className="fixed inset-0 dark z-50 bg-zinc-950/95 backdrop-blur-md flex items-center justify-center p-0  animate-in fade-in duration-200">
          <div className="bg-card w-full h-full md:w-[100vw] md:h-[100vh]  shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between bg-muted/40 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-primary/10 text-primary rounded-lg shrink-0">
                  <FileText className="size-5 animate-pulse" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-sm truncate max-w-[200px] sm:max-w-md md:max-w-xl lg:max-w-3xl text-foreground">
                    {previewFile.name}
                  </h3>
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                    Stitched Output Preview •{" "}
                    {formatBytes(previewFile.resultBytes?.length || 0)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-5 shrink-0">
                <Button
                  onClick={() => downloadResult(previewFile)}
                  className="bg-green-700 hover:bg-green-800 active:bg-green-800 text-white font-bold text-xs h-9 px-4 gap-1.5 shadow-sm rounded-lg transition-colors cursor-pointer"
                >
                  <Download className="size-4" />
                  <span className="hidden sm:inline">Download PDF</span>
                  <span className="sm:hidden">Download</span>
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPreviewFileId(null)}
                  className="rounded-lg size-9 border border-border hover:bg-muted/80 active:bg-muted text-foreground hover:text-foreground cursor-pointer"
                >
                  <X className="size-4" />
                </Button>
              </div>
            </div>

            {/* Live Controls Toolbar inside Preview */}
            <div className="px-6 py-2.5 border-b bg-card/65 backdrop-blur-xs flex flex-wrap items-center justify-center gap-8 shrink-0 select-none">
              {/* Split Ratio Slider in Preview */}
              <div className="flex items-center gap-2 min-w-[220px]">
                <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
                  Split Ratio:
                </span>
                <input
                  type="range"
                  min="0.4"
                  max="0.8"
                  step="0.005"
                  value={previewFile.splitRatio}
                  onChange={(e) =>
                    updatePreviewSetting({
                      splitRatio: parseFloat(e.target.value),
                    })
                  }
                  className="accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer w-[17vw]"
                />
                <span className="text-xs font-mono font-bold text-primary whitespace-nowrap min-w-[40px] text-right">
                  {(previewFile.splitRatio * 100).toFixed(1)}%
                </span>
              </div>

              {/* Left Padding Slider in Preview */}
              <div className="flex items-center gap-2 min-w-[240px]">
                <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
                  Left Padding:
                </span>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={previewFile.leftPadding || 0}
                  onChange={(e) =>
                    updatePreviewSetting({
                      leftPadding: parseInt(e.target.value, 10),
                    })
                  }
                  className="accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer w-[17vw]"
                />
                <span className="text-xs font-mono font-bold text-primary whitespace-nowrap min-w-[40px] text-right">
                  {previewFile.leftPadding || 0} pt
                </span>
              </div>

              {/* Right Padding Slider in Preview */}
              <div className="flex items-center gap-2 min-w-[240px]">
                <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
                  Right Padding:
                </span>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={previewFile.rightPadding || 0}
                  onChange={(e) =>
                    updatePreviewSetting({
                      rightPadding: parseInt(e.target.value, 10),
                    })
                  }
                  className="accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer w-[17vw]"
                />
                <span className="text-xs font-mono font-bold text-primary whitespace-nowrap min-w-[40px] text-right">
                  {previewFile.rightPadding || 0} pt
                </span>
              </div>

              {/* Gutter Slider in Preview */}
              <div className="flex items-center gap-2 min-w-[220px] hidden">
                <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
                  Gutter (space):
                </span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={previewFile.gutter}
                  onChange={(e) =>
                    updatePreviewSetting({
                      gutter: parseInt(e.target.value, 10),
                    })
                  }
                  className="accent-primary h-1 bg-muted rounded-lg appearance-none cursor-pointer w-24"
                />
                <span className="text-xs font-mono font-bold text-primary whitespace-nowrap min-w-[40px] text-right">
                  {previewFile.gutter} pt
                </span>
              </div>
            </div>

            {/* PDF View Frame */}
            <div className="flex-1 bg-zinc-900 p-1 md:p-3 flex items-center justify-center">
              <iframe
                src={`${previewUrl}#toolbar=0&view=Fit&scrollbar=0&navpanes=0`}
                title="Stitched PDF Preview"
                className="w-full h-full rounded-md md:rounded border border-white/5 shadow-2xl bg-zinc-950"
              />
            </div>
          </div>
        </div>
      )}

      {/* Terminal Log Dashboard */}
      <footer
        className={clsx(
          "relative z-10 w-full max-w-[100vw] px-8 pb-6 shrink-0 mt-auto",
          {
            hidden: !logsVisible,
          },
        )}
      >
        <div className="border border-border bg-card/60 backdrop-blur-md rounded-lg overflow-hidden ">
          {/* Header */}
          <div
            onClick={() => setLogOpen(!logOpen)}
            className="px-4 py-2 flex items-center justify-between bg-muted/40 cursor-pointer select-none"
          >
            <div className="flex items-center gap-2">
              <TerminalIcon className="size-4 text-primary" />
              <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-muted-foreground">
                Execution Logs
              </span>
              <span className="text-[10px] bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.2 rounded font-mono font-bold">
                {logs.length}
              </span>
            </div>
            <div
              className="flex items-center gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              <label className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoScrollLogs}
                  onChange={(e) => setAutoScrollLogs(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary/40 size-3"
                />
                Auto-scroll
              </label>
              <button
                type="button"
                onClick={clearLogs}
                className="text-[10px] font-mono text-muted-foreground hover:text-primary transition-colors hover:underline"
              >
                Clear
              </button>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground text-xs"
                onClick={() => setLogOpen(!logOpen)}
              >
                {logOpen ? (
                  <X className="size-4" />
                ) : (
                  <ChevronRight className="size-4 rotate-90" />
                )}
              </button>
            </div>
          </div>

          {/* Details */}
          {logOpen && (
            <div
              ref={logContainerRef}
              className="p-3 h-24 overflow-y-auto bg-black/90 text-zinc-300 font-mono text-[11px] flex flex-col gap-1 shadow-inner"
            >
              {logs.length === 0 ? (
                <div className="text-zinc-600 italic select-none h-full flex items-center justify-center">
                  Terminal ready. Upload a file or adjust settings to see logs.
                </div>
              ) : (
                logs.map((log) => {
                  let badgeColor = "text-zinc-500";
                  if (log.type === "success") badgeColor = "text-green-400";
                  if (log.type === "error")
                    badgeColor = "text-red-400 font-bold";
                  if (log.type === "warn") badgeColor = "text-amber-400";

                  return (
                    <div
                      key={log.id}
                      className="flex gap-2.5 items-start leading-relaxed break-all"
                    >
                      <span className="text-zinc-500 shrink-0 select-none">
                        [{log.timestamp}]
                      </span>
                      <span className={`${badgeColor} shrink-0 select-none`}>
                        {log.type.toUpperCase()}:
                      </span>
                      <span className="text-zinc-200 select-text">
                        {log.text}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

// Visual Page Thumbnail renderer using canvas & PDFJS
interface PageThumbnailProps {
  pdfDoc: any; // PDFJS doc object
  pageNumber: number; // 1-based page number
  side: "left" | "right";
  splitRatio: number;
  isExcluded: boolean;
  zoom: "sm" | "md" | "lg";
  onClick: () => void;
}

function PageThumbnail({
  pdfDoc,
  pageNumber,
  side,
  splitRatio,
  isExcluded,
  zoom,
  onClick,
}: PageThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasRendered, setHasRendered] = useState(false);

  useEffect(() => {
    let active = true;
    if (!pdfDoc) {
      setHasRendered(false);
      return;
    }

    const renderPage = async () => {
      try {
        setLoading(true);
        const page = await pdfDoc.getPage(pageNumber);
        if (!active) return;

        // Choose scale factor based on the visual zoom setting (increased 2x for readability)
        let scale = 0.32;
        if (zoom === "md") scale = 0.56;
        if (zoom === "lg") scale = 1.1; // Highly readable resolution for large thumbnails

        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext("2d");
        if (!context) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };
        await page.render(renderContext).promise;
        if (active) {
          setLoading(false);
          setHasRendered(true);
        }
      } catch (err) {
        console.error("Error rendering thumbnail:", err);
        if (active) {
          setLoading(false);
        }
      }
    };

    renderPage();
    return () => {
      active = false;
    };
  }, [pdfDoc, pageNumber, zoom]);

  const showPlaceholder = !pdfDoc || loading || !hasRendered;

  return (
    <div
      onClick={onClick}
      className={`relative border rounded-[21px] overflow-hidden aspect-[3/4.2] cursor-pointer group transition-all duration-200 select-none shadow-xs hover:shadow-md ${
        isExcluded
          ? "border-destructive/0 bg-destructive/5 hover:border-destructive/0"
          : "bg-card"
        // : "border-border hover:border-primary/80 bg-card"
      }`}
    >
      {/* Canvas for PDF Page render */}
      {pdfDoc && (
        <canvas
          ref={canvasRef}
          className={`w-full h-full rounded-[21px] object-cover transition-opacity duration-300 ${
            showPlaceholder ? "opacity-0 absolute" : "opacity-100"
          }`}
        />
      )}

      {/* Fallback placeholder (rendered offline or during loading) */}
      {showPlaceholder && (
        <div className="w-full h-full p-2 flex flex-col justify-between bg-card text-card-foreground">
          <div className="flex justify-between items-center text-[8px] text-muted-foreground border-b pb-1 font-mono">
            <span>PAGE</span>
            <span>{pageNumber}</span>
          </div>
          <div className="flex flex-col gap-1.5 py-2 grow justify-center opacity-30">
            <div className="h-0.5 bg-current rounded w-3/4" />
            <div className="h-0.5 bg-current rounded w-5/6" />
            <div className="h-0.5 bg-current rounded w-2/3" />
            <div className="h-0.5 bg-current rounded w-4/5" />
          </div>
          <div className="text-[9px] text-right font-bold font-mono">
            p. {pageNumber}
          </div>
        </div>
      )}

      {/* Grid overlay showing Crop visual indication with real-time splitRatio divider */}
      {!isExcluded && (
        <div className="absolute rounded-[21px] inset-0 flex pointer-events-none z-10">
          {/* Left half */}
          <div
            // backdrop-blur-[0.5px]
            className={`transition-all duration-100 ${
              side === "left"
                ? "bg-primary/10 border-r border-dashed border-primary/50"
                : "bg-black/45  border-r "
            }`}
            style={{ width: `${splitRatio * 100}%` }}
          />
          {/* Right half */}
          <div
            // backdrop-blur-[0.5px]
            className={`transition-all duration-100 ${
              side === "right" ? "bg-transparent" : "bg-black/45 "
            }`}
            style={{ width: `${(1 - splitRatio) * 100}%` }}
          />
        </div>
      )}

      {/* Skip/Exclude state overlay */}
      {/* backdrop-blur-[1px] */}
      {isExcluded && (
        <div className="absolute inset-0 bg-black/70  flex flex-col items-center justify-center text-destructive font-bold text-xs pointer-events-none z-25">
          <span className="bg-destructive text-white px-1.5 py-0.5 rounded text-[12px] font-mono uppercase tracking-wider mb-1">
            Excluded
          </span>
          <span className="text-white text-[12px] opacity-70">
            Page {pageNumber}
          </span>
        </div>
      )}

      {/* Page number badge overlay */}
      {!isExcluded && (
        <div className="absolute top-2 right-2 bg-background/95 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-full border shadow-sm pointer-events-none transition-colors z-20">
          {/* #&nbsp; */}
          {pageNumber}
        </div>
      )}
    </div>
  );
}

export default App;
